import { edgeFunctionErrorMessage } from "@/lib/edge-function-error";
import { supabase, supabaseAnonKey } from "@/lib/supabase";

type GenerateEmailReplyResponse = {
  action?: unknown;
  replyBody?: unknown;
  reason?: unknown;
  error?: unknown;
  requestId?: unknown;
};

export type GenerateEmailReplyResult = {
  action: "draft" | "no_reply";
  replyBody: string;
  reason: string;
};

function readResult(
  payload: GenerateEmailReplyResponse | null
): GenerateEmailReplyResult {
  if (payload?.action === "no_reply") {
    if (typeof payload.reason !== "string" || !payload.reason.trim()) {
      throw new Error("The reply draft service returned no recommendation");
    }
    return { action: "no_reply", replyBody: "", reason: payload.reason.trim() };
  }
  if (payload?.action !== "draft") {
    throw new Error("The reply draft service returned an invalid action");
  }
  if (typeof payload.replyBody !== "string" || !payload.replyBody.trim()) {
    throw new Error("The reply draft service returned no reply body");
  }
  return {
    action: "draft",
    replyBody: payload.replyBody,
    reason: typeof payload.reason === "string" ? payload.reason.trim() : "",
  };
}

/** Local dev override so the composer can hit a function served via `supabase functions serve`. */
function localFunctionUrl(): string {
  if (!import.meta.env.DEV) return "";

  const configured = (
    import.meta.env.VITE_GENERATE_EMAIL_REPLY_URL ?? ""
  ).trim();
  if (!configured) return "";

  let url: URL;
  try {
    url = new URL(configured);
  } catch {
    throw new Error("VITE_GENERATE_EMAIL_REPLY_URL must be a valid URL");
  }
  if (
    url.protocol !== "http:" ||
    !["127.0.0.1", "localhost"].includes(url.hostname)
  ) {
    throw new Error("The local reply draft URL must use localhost");
  }
  return url.toString();
}

/**
 * Generate a draft without saving or sending it. `adminInstruction` is
 * optional — the coliviq Agent panel drafts automatically on open with no
 * instruction field; the server defaults it when omitted (see
 * resolveAdminInstruction in the shared edge-function module).
 */
export async function generateEmailReply(
  issueId: string,
  adminInstruction?: string
): Promise<GenerateEmailReplyResult> {
  const localUrl = localFunctionUrl();
  const body = { issueId, adminInstruction: adminInstruction?.trim() ?? "" };

  if (!localUrl) {
    const { data, error } =
      await supabase.functions.invoke<GenerateEmailReplyResponse>(
        "generate-email-reply",
        { body }
      );
    if (error) {
      throw new Error(
        await edgeFunctionErrorMessage(error, "Unable to generate reply draft")
      );
    }
    return readResult(data);
  }

  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();
  if (sessionError || !session?.access_token) {
    throw new Error("An authenticated staff session is required");
  }

  const response = await fetch(localUrl, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      apikey: supabaseAnonKey,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  let payload: GenerateEmailReplyResponse | null = null;
  try {
    payload = (await response.json()) as GenerateEmailReplyResponse;
  } catch {
    // Use the status-based fallback rather than exposing an HTML/runtime body.
  }

  if (!response.ok) {
    const serverMessage =
      typeof payload?.error === "string" ? payload.error : null;
    throw new Error(
      serverMessage ?? `Reply draft generation failed (${response.status})`
    );
  }

  return readResult(payload);
}
